import { gql } from '@apollo/client';
import * as React from 'react';

import { useBreakpoint } from '../../../hooks/useBreakpoint';
import { CalendarIcon, EditIcon } from '../../../icons';
import AccountMetadata from '../../AccountMetadata';
import Dropdown from '../../base/Dropdown';
import { isUnhealthyBankConnectionMetadata } from '../../CalloutCard/cards/UnhealthyBankConnectionCalloutCard';
import { DateRangePicker, DateRangePickerModal, type CalendarDateRange } from '../../DateRangePicker';
import Entity from '../../entity';
import PlaidFixConnectionButton from '../../plaid/PlaidFixConnectionButton';
import UpdateAccountModal from '../../UpdateAccountModal';

import type { AccountPaneHeaderFragment } from './__generated__/AccountPaneHeader.gql';

const AccountPaneHeaderFragment = gql`
  fragment AccountPaneHeader on FinancialAccount {
    id
    name
    subtype
    mask
    ...EntityAvatar
    bankConnection {
      id
      lastSyncedAt
      unhealthyConnectionCallout {
        id
        metadata
      }
    }
    bank {
      id
      name
    }
  }
  ${Entity.Avatar.Fragment}
`;

interface IAccountPaneHeaderProps {
  financialAccount: AccountPaneHeaderFragment;
  dateRange?: CalendarDateRange;
  onDateRangeChange?: (dateRange: CalendarDateRange) => void;
}

const AccountPaneHeader = ({ financialAccount, dateRange, onDateRangeChange }: IAccountPaneHeaderProps) => {
  const isMdOrUp = useBreakpoint('md');
  const [isUpdateModalOpen, setIsUpdateModalOpen] = React.useState(false);
  const [isDateRangeModalOpen, setIsDateRangeModalOpen] = React.useState(false);

  // Check if bank connection has an unhealthy callout requiring login
  const unhealthyCallout = financialAccount.bankConnection?.unhealthyConnectionCallout;
  const needsReconnection =
    unhealthyCallout &&
    isUnhealthyBankConnectionMetadata(unhealthyCallout.metadata) &&
    unhealthyCallout.metadata.subtype === 'login_required';

  return (
    <>
      <Entity.Header
        title={financialAccount.name}
        subtitle={
          <AccountMetadata
            subtype={financialAccount.subtype}
            bankName={financialAccount.bank.name}
            mask={financialAccount.mask}
            lastSyncedAt={financialAccount.bankConnection?.lastSyncedAt ?? undefined}
          />
        }
        avatar={<Entity.Avatar entity={financialAccount} size="xl" />}
        trailingContent={
          <div className="flex items-center gap-3">
            {needsReconnection && (
              <PlaidFixConnectionButton bankConnectionId={financialAccount.bankConnection.id} size="sm" />
            )}
            {isMdOrUp && <DateRangePicker value={dateRange} onChange={onDateRangeChange} defaultValue="past6Months" />}
            <Dropdown.Root>
              <Dropdown.DotsButton className="p-2" />
              <Dropdown.Popover className="w-min">
                <Dropdown.Menu>
                  {!isMdOrUp && (
                    <Dropdown.Item icon={CalendarIcon} onAction={() => setIsDateRangeModalOpen(true)}>
                      Time range&hellip;
                    </Dropdown.Item>
                  )}
                  <Dropdown.Item icon={EditIcon} onAction={() => setIsUpdateModalOpen(true)}>
                    Edit account&hellip;
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown.Root>
          </div>
        }
      />
      <UpdateAccountModal account={financialAccount} isOpen={isUpdateModalOpen} onOpenChange={setIsUpdateModalOpen} />
      {!isMdOrUp && (
        <DateRangePickerModal
          isOpen={isDateRangeModalOpen}
          onOpenChange={setIsDateRangeModalOpen}
          value={dateRange}
          onChange={onDateRangeChange}
          defaultValue="past6Months"
        />
      )}
    </>
  );
};

AccountPaneHeader.Fragment = AccountPaneHeaderFragment;

export default AccountPaneHeader;
